import React, { MutableRefObject, Suspense, useContext, useEffect, useRef, useState } from "react";
import { CancelTokenSource } from "axios";
import { Location } from "history";
import { Button } from "react-bootstrap";
import { TFunction, useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { wrapPromise } from "go-core";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import RenderLimitedText from "go-core/components/RenderLimitedText";
import { useWindowSize } from "go-core/components/useWindowSize";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig } from "go-list/list/services/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext, SegmentListService } from "go-segment/context";
import { ClientDetailsForm } from "../../components/ClientDetailsForm";
import ClientSecretModal from "../../components/ClientSecretModal";
import { ClientDetailsApi, ClientDetailsImportContext } from "../../services/types";

interface ListProps {
	resource: any;
	requests: ClientDetailsImportContext;
}

interface ClientDetailsProps {
	requests: ClientDetailsImportContext;
}

const listName = "CLIENT_DETAILS";
const resourceType = "CLIENT_DETAILS";

const buildConfig = (
	t: TFunction<"translation", undefined>,
	data: any,
	segmentListService: SegmentListService,
	deleteClientDetailHandler: (clientDetailId: string) => Promise<void>,
	updateClientDetailHandler: (data: ClientDetailsApi) => void,
	getClientDetails: (params?: Record<string, any>, options?: Record<string, any>) => Promise<ClientDetailsApi[]>,
	listParams: MutableRefObject<Record<string, any>>,
	handleShowClientSecret: (clientId: string) => void,
	location: Location
) => {
	let config = {
		fields: [
			{
				id: "client_id",
				name: t("lib:go_component.client_details.field.client_id.title"),
			},
			{
				id: "client_secret",
				name: t("lib:go_component.client_details.field.client_secret.title"),
				render: (item: ClientDetailsApi) => (
					<div className="d-flex align-items-center gap-2">
						<p className="mb-0">{item.client_secret}</p>
						<Button onClick={() => handleShowClientSecret(item.client_id)}>
							{t("lib:go_component.client_details.action.show.title")}
						</Button>
					</div>
				),
				renderExport: (item: ClientDetailsApi) => item.client_secret,
			},
			{
				id: "scopes",
				name: "Scopes",
				render: (item: ClientDetailsApi) => (
					<RenderLimitedText minWidth={200}>{item.scopes.join(", ")}</RenderLimitedText>
				),
				renderExport: (item: ClientDetailsApi) => item.scopes.join(", "),
			},
			{
				id: "additional_information",
				name: t("lib:go_component.client_details.field.additional_information.title"),
				render: (item: ClientDetailsApi) =>
					item.additional_information.map((additionalInformation) => {
						return (
							<React.Fragment key={additionalInformation.name}>
								<span>{`${additionalInformation.name}: ${additionalInformation.value}`}</span>
								<br />
							</React.Fragment>
						);
					}),
				renderExport: (item: ClientDetailsApi) =>
					item.additional_information
						.map((additionalInformation) => {
							return `${additionalInformation.name}: ${additionalInformation.value}`;
						})
						.join(", "),
			},
			{
				id: "authorized_grant_types",
				name: "Grant types",
				render: (item: ClientDetailsApi) => (
					<RenderLimitedText minWidth={200}>{item.authorized_grant_types.join(", ")}</RenderLimitedText>
				),
				renderExport: (item: ClientDetailsApi) => item.authorized_grant_types.join(", "),
			},
		],
		actions: [
			{
				name: t("common.action.remove", { ns: "lib" }),
				click: (item: ClientDetailsApi) => deleteClientDetailHandler(item.client_id),
				refreshListAfter: true,
				confirmation: { use: true, message: t("confirmation.message.remove", { ns: "lib" }) },
			},
			{
				name: t("common.action.edit", { ns: "lib" }),
				click: (item: ClientDetailsApi) => updateClientDetailHandler(item),
			},
			{
				name: t("lib:go_component.client_details.action.edit_grant_types.title"),
				click: (item: ClientDetailsApi) => updateClientDetailHandler(item),
				visible: (item: ClientDetailsApi) => item.authorized_grant_types.includes("organization"),
				link: (item: ClientDetailsApi) => `${location.pathname}/${item.client_id}/client_grants`,
			},
		],
		selectedColumns: ["client_id", "client_secret", "scopes", "additional_information", "authorized_grant_types"],
		segments: [
			{
				id: "all",
				name: t("lib:common.word.all"),
				slug: "all",
			},
		],
		filters: [],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		fetch: (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			listParams.current = params;
			return getClientDetails(params, { cancelToken: sourceToken?.token });
		},
		saveSegment: (segment: SegmentType) => {
			return segmentListService.save(listName, resourceType, segment);
		},
		withoutExportActions: true,
	} as ListConfig;
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		customFields: data.fields,
		filterValues: data.filter_values,
	};
	return config;
};

const List = ({ resource, requests }: ListProps) => {
	const [responseData, setResponseData] = useState<ClientDetailsApi[]>([]);
	const [openModal, setOpenModal] = useState<ClientDetailsApi | undefined>(undefined);
	const segmentContextValue = useContext(SegmentContext);
	const { addSuccessFlash } = useFlash();
	const listParams = useRef<Record<string, any>>({});
	const { getClientDetails, deleteClientDetail } = requests;
	const { t } = useTranslation();
	const isMobile = useWindowSize().isMobile;
	const [clientSecretToShow, setClientSecretToShow] = useState<string>("");
	const location = useLocation();

	if (!resource) return null;

	const updateClientDetailHandler = (data: ClientDetailsApi) => setOpenModal(data);

	const createClientDetailHandler = () => setOpenModal({} as ClientDetailsApi);

	const deleteClientDetailHandler = async (clientDetailId: string) => {
		await deleteClientDetail(clientDetailId);
		addSuccessFlash(t("common.flash.removed", { ns: "lib" }));
	};

	const handleShowClientSecret = (clientId: string) => {
		setClientSecretToShow(clientId);
	};

	const data = resource.read();
	const config = buildConfig(
		t,
		data,
		segmentContextValue,
		deleteClientDetailHandler,
		updateClientDetailHandler,
		getClientDetails,
		listParams,
		handleShowClientSecret,
		location
	);

	const onRefresh = async () => {
		if (config.fetch) {
			const res = await config.fetch(listParams.current);
			setResponseData(res);
		}
	};

	const onModalHide = () => {
		setOpenModal(undefined);
		onRefresh();
	};

	const buttons: ButtonProps[] = [
		{
			title: t("common.action.add", { ns: "lib" }),
			action: createClientDetailHandler,
			variant: "primary",
		},
	];

	const mobileActions: MobileActionProps[] = [
		{
			title: t("common.action.add", { ns: "lib" }),
			action: createClientDetailHandler,
		},
	];

	return (
		<>
			{!!clientSecretToShow && (
				<ClientSecretModal
					clientId={clientSecretToShow}
					show={!!clientSecretToShow}
					onHide={() => setClientSecretToShow("")}
					requests={requests}
				/>
			)}
			{openModal && <ClientDetailsForm requests={requests} data={openModal} onHide={onModalHide} />}
			{!isMobile && <Header title={t("lib:go_component.client_details.header.title")} buttons={buttons} />}
			<Suspense fallback={<LoadingContainer />}>
				<ListData data={responseData} config={config} onFetch={setResponseData} mobileActions={mobileActions} />
			</Suspense>
		</>
	);
};

export const ClientDetailsList = ({ requests }: ClientDetailsProps): JSX.Element => {
	const [resource, setResource] = useState<any>();
	const segmentContextValue = useContext(SegmentContext);
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const { t } = useTranslation();

	useEffect(() => {
		handleChangeTabTitle(t("lib:go_component.client_details.header.title"));
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType)));
	}, []);

	return (
		<Suspense fallback={<LoadingContainer />}>
			<List resource={resource} requests={requests} />
		</Suspense>
	);
};
